import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Yup from "yup";
import { createCoupons, updateCoupons } from "../../features/admin/CouponSlice";
import { toast } from "react-toastify";
import { fetchProducts } from "../../features/admin/ProductSlice";
import { formatCurrencyVND, formatDateReverse } from "../../utils/helps";
import DatePicker from "../DatePicker";

const couponSchema = Yup.object().shape({
  code: Yup.string().trim().required("Mã giảm giá không được để trống"),
  price: Yup.number()
    .positive("Giá phải là số dương")
    .required("Giá không được để trống"),
  userId: Yup.number()
    .required("Người dùng không được để trống")
    .positive("Người dùng không hợp lệ"),
  productId: Yup.number()
    .required("Sản phẩm không được để trống")
    .positive("Sản phẩm không hợp lệ"),
  startDate: Yup.date()
    .required("Ngày bắt đầu không được để trống")
    .min(
      new Date().toISOString().slice(0, 10),
      "Ngày bắt đầu phải từ hôm nay trở đi"
    )
    .typeError("Ngày bắt đầu không hợp lệ"),
  endDate: Yup.date()
    .required("Ngày kết thúc không được để trống")
    .min(Yup.ref("startDate"), "Ngày kết thúc phải sau ngày bắt đầu")
    .typeError("Ngày kết thúc không hợp lệ"),
});

const fieldLabels = {
  code: "Mã thưởng",
  price: "Giá tiền",
  userId: "Người dùng",
  productId: "Sản phẩm",
  startDate: "Ngày bắt đầu",
  endDate: "Ngày kết thúc",
};

const CouponModal = ({ coupon, onClose }) => {
  const [formData, setFormData] = useState({
    code: "",
    price: 0.0,
    userId: 0,
    productId: 0,
    startDate: "",
    endDate: "",
  });
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users?.users?.data?.users);
  const { products } = useSelector((state) => state.products);

  useEffect(() => {
    if (coupon) {
      setFormData(coupon);
    }
  }, [coupon]);

  useEffect(() => {
    dispatch(fetchProducts());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await couponSchema.validate(formData, { abortEarly: false });
      if (coupon) {
        await dispatch(
          updateCoupons({ id: coupon.id, newPost: formData })
        ).unwrap();
        toast.success("Cập nhật thành công!");
        onClose();
      } else {
        await dispatch(createCoupons(formData)).unwrap();
        toast.success("Thêm mã thưởng mới thành công!");
        onClose();
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
        console.error("Lỗi khi lưu mã thưởng:", error);
      }
    }
  };
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold ">
              {coupon ? "Chỉnh sửa thông tin mã thưởng" : "Thêm mã thưởng mới"}
            </h2>
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            >
              X
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="flex gap-10 mb-5">
              <div className="w-full">
                <label className="block text-sm font-medium mb-1">
                  {fieldLabels.code}
                  <i className="text-red-600"> *</i>
                </label>
                <input
                  type="text"
                  name={formData.code}
                  value={formData.code}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      code: e.target.value,
                    });
                    setErrors({ ...errors, code: "" });
                  }}
                  className={`w-full p-2 border rounded-md ${
                    errors.code ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {errors.code && (
                  <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                )}
              </div>
              <div className="w-full">
                <label className="block text-sm font-medium  mb-1">
                  {fieldLabels.price}
                  <i className="text-red-600"> *</i>
                </label>
                <input
                  type="text"
                  name={formData.price}
                  value={formatCurrencyVND(formData.price)}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, "");

                    setFormData((prev) => ({
                      ...prev,
                      price: rawValue === "" ? "" : Number(rawValue),
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      price: "",
                    }));

                    setTimeout(() => {
                      const inputElement = e.target;
                      const formattedValue = formatCurrencyVND(
                        Number(rawValue)
                      );

                      const position = formattedValue.length - 2;
                      inputElement.setSelectionRange(position, position);
                    }, 0);
                  }}
                  className={`w-full p-2 border rounded-md ${
                    errors.price ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                )}
              </div>
            </div>
            <div className="flex gap-10 mb-5">
              <div className="w-full">
                <label className="block text-sm font-medium mb-1">
                  {fieldLabels.userId}
                  <i className="text-red-600"> *</i>
                </label>
                <select
                  name={formData.userId}
                  value={formData.userId}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      userId: e.target.value,
                    });
                    setErrors({
                      ...errors,
                      userId: "",
                    });
                  }}
                  className={`w-full p-2 border rounded-md ${
                    errors.userId ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="">Chọn người dùng</option>
                  {users?.map((item, idx) => (
                    <option key={idx} value={item.id}>
                      {item.userFullName}
                    </option>
                  ))}
                </select>
                {errors.userId && (
                  <p className="mt-1 text-sm text-red-600">{errors.userId}</p>
                )}
              </div>
              <div className="w-full">
                <label className="block text-sm font-medium  mb-1">
                  {fieldLabels.productId}
                  <i className="text-red-600"> *</i>
                </label>
                <select
                  name={formData.productId}
                  value={formData.productId}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      productId: e.target.value,
                    });
                    setErrors({
                      ...errors,
                      productId: "",
                    });
                  }}
                  className={`w-full p-2 border rounded-md ${
                    errors.productId ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="">Chọn sản phẩm</option>
                  {products.map((item, idx) => (
                    <option key={idx} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {errors.productId && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.productId}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-10 mb-5">
              {/* Ngày bắt đầu */}
              {/* <div className="w-full">
                <label className="block text-sm font-medium mb-1">
                  {fieldLabels.startDate}
                  <i className="text-red-600"> *</i>
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formatDateReverse(formData.startDate ?? "")}
                  onChange={(e) => {
                    setFormData({ ...formData, startDate: e.target.value });
                    setErrors({ ...errors, startDate: "" });
                  }}
                  className={`w-full p-2 border rounded-md ${
                    errors.startDate ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.startDate}
                  </p>
                )}
              </div> */}

              <div className="w-full">
                <label className="block text-sm font-medium mb-1">
                  {fieldLabels.startDate}
                  <i className="text-red-600"> *</i>
                </label>
                <DatePicker
                  name="startDate"
                  value={formData.startDate}
                  onChange={(name, value) => {
                    setFormData({ ...formData, startDate: value });
                    setErrors({ ...errors, startDate: "" });
                  }}
                  title={fieldLabels.startDate}
                  error={errors.startDate}
                />
                {errors.startDate && (
                  <p className="text-red-500 text-sm">{errors.startDate}</p>
                )}
              </div>

              {/* Ngày kết thúc */}
              {/* <div className="w-full">
                <label className="block text-sm font-medium mb-1">
                  {fieldLabels.endDate}
                  <i className="text-red-600"> *</i>
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formatDateReverse(formData.endDate ?? "")}
                  onChange={(e) => {
                    setFormData({ ...formData, endDate: e.target.value });
                    setErrors({ ...errors, endDate: "" });
                  }}
                  className={`w-full p-2 border rounded-md ${
                    errors.endDate ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                )}
              </div> */}

              <div className="w-full">
                <label className="block text-sm font-medium mb-1">
                  {fieldLabels.endDate}
                  <i className="text-red-600"> *</i>
                </label>
                <DatePicker
                  name="endDate"
                  value={formData.endDate}
                  onChange={(name, value) => {
                    setFormData({ ...formData, endDate: value });
                    setErrors({ ...errors, endDate: "" });
                  }}
                  title={fieldLabels.endDate}
                  error={errors.endDate}
                />
                {errors.endDate && (
                  <p className="text-red-500 text-sm">{errors.endDate}</p>
                )}
              </div>
            </div>

            {/* Footer - Action Buttons */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                {coupon ? "Cập nhật" : "Thêm mới"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CouponModal;
